/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { View, Text, Skeleton, Icon } from '@az/starc-ui';
import { Warning } from '@az/starc-ui-icons';
import { useStoreDetails } from '../../api/getStoreDetails';
import { StoreHours } from '../StoreHours/StoreHours';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { useStoreDayInfo } from '../../hooks/useStoreDayInfo';
import { HeadlineUnderline } from '@/components/HeadlineUnderline/HeadlineUnderline';
import { type YextHours } from '../../interface/yext-types';
import { StoreCard } from '../StoreCard/StoreCard';
import { StoreCardSkeleton } from '../StoreCardSkeleton/StoreCardSkeleton';
import cx from 'classnames';
import styles from './StoreInformation.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { type BottomSheetVariant } from '../../interface/types';
import starcClassOverrides from '@/theme/starcClassOverrides.module.scss';
import { type ContentStackStorePageBody } from '@/features/contentstack/interface';
import { useLocationsParams } from '../../hooks/useLocationsParams';
import { pickStoreWarning } from '../../utils/pickStoreWarning';

const LazyLocationsInfoBottomSheet = dynamic(() =>
  import('../LocationsInfoBottomSheet/LocationsInfoBottomSheet').then(
    (mod) => mod.LocationsInfoBottomSheet,
  ),
);

const emptyStoreHours: YextHours = {
  monday: {
    openIntervals: [],
  },
  tuesday: {
    openIntervals: [],
  },
  wednesday: {
    openIntervals: [],
  },
  thursday: {
    openIntervals: [],
  },
  friday: {
    openIntervals: [],
  },
  saturday: {
    openIntervals: [],
  },
  sunday: {
    openIntervals: [],
  },
};
const labelMap = {
  lblStoreHours: 'label_locations_store_details_store_hours',
  lblContactInfo: 'label_locations_store_details_contact_info',
};

type StoreInformationProps = {
  storeInformation?: ContentStackStorePageBody['store_information'];
};

export function StoreInformation({ storeInformation }: StoreInformationProps) {
  const { data: storeDetails } = useStoreDetails();
  const labels = useLabels(labelMap);
  const { state, city } = useLocationsParams();

  const cmsWarningText =
    pickStoreWarning(storeInformation?.warning_notifications, { state, city }) ??
    storeInformation?.warning_notification;
  const alertText = storeDetails?.additionalHoursText?.trim() || cmsWarningText?.trim();

  const [bottomSheetVariant, setBottomSheetVariant] = useState<BottomSheetVariant | null>(null);

  const { today } = useStoreDayInfo({
    hours: storeDetails?.hours,
    closed: storeDetails?.closed,
    timezone: storeDetails?.timezone,
    holidayHours: storeDetails?.hours?.holidayHours,
  });

  return (
    <View>
      {storeDetails?.name ? (
        <View>
          <Text className={styles.storeTitle} variant="display-2" as="h1" textCase="uppercase">
            <View as="span">
              <View as="span">{storeDetails.name}</View>
              <View as="span"> {storeDetails.geomodifier}</View>
            </View>
          </Text>
          <HeadlineUnderline />
        </View>
      ) : (
        <View>
          <View height="35px" attributes={{ 'data-testid': 'store-name-skeleton' }}>
            <Skeleton height="30px" width="80%" />
          </View>
          <View height="35px">
            <Skeleton height="30px" width="60%" />
          </View>
        </View>
      )}

      <FlexGrid gap={5} mobileColumn className={styles.storeInfoContainer}>
        <View className={cx(styles.gridItem, styles.storeCard)}>
          <Text
            variant="display-4"
            weight="medium"
            className={cx(styles.title, starcClassOverrides['starc-display-4'])}
          >
            {labels.lblContactInfo}
          </Text>
          <div className={styles.contentWrapper}>
            {storeDetails ? (
              <StoreCard
                store={storeDetails}
                isStoreDetailsCard
                openBottomSheet={(variant) => {
                  setBottomSheetVariant(variant);
                }}
              />
            ) : (
              <StoreCardSkeleton />
            )}
          </div>
        </View>
        <View className={cx(styles.gridItem, styles.storeHours)}>
          <div className={styles.storeHoursHeader}>
            <Text
              variant="display-4"
              weight="medium"
              className={cx(styles.title, starcClassOverrides['starc-display-4'])}
            >
              {labels.lblStoreHours}
            </Text>
            {alertText && (
              <div className={styles.weatherWarning}>
                <Icon svg={Warning} color="warning" size={6} />
                <Text
                  variant="small-body"
                  className={cx(styles.weatherWarningText, starcClassOverrides['starc-small-body'])}
                >
                  {alertText}
                </Text>
              </div>
            )}
          </div>
          <div className={styles.contentWrapper}>
            <StoreHours
              hours={storeDetails?.hours ?? emptyStoreHours}
              today={today}
              holidayHours={storeDetails?.hours?.holidayHours}
              storeTimezone={storeDetails?.timezone}
              reopenDate={storeDetails?.hours?.reopenDate}
            />
          </div>
        </View>
      </FlexGrid>
      {bottomSheetVariant && storeDetails ? (
        <LazyLocationsInfoBottomSheet
          variant={bottomSheetVariant}
          storeDetails={storeDetails}
          onClose={() => {
            const buttonID =
              bottomSheetVariant === 'navigation'
                ? 'locations-directions-button'
                : 'locations-contacts-button';

            setBottomSheetVariant(null);

            const button = document.getElementById(buttonID);
            if (button) {
              button.focus();
            }
          }}
        />
      ) : null}
    </View>
  );
}
